import { useState } from 'react';
import { Card, Input, Button, Table, Tag, message, Space, Typography, Alert, Statistic } from 'antd';
import { DownloadOutlined, SearchOutlined } from '@ant-design/icons';
import { api, getToken } from '../../api';

const { TextArea } = Input;
const { Text } = Typography;

export default function DocDownload() {
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [downloading, setDownloading] = useState(false);

  const parseBatchNumbers = () =>
    input.split(/[\n,，;；\s]+/).map((s) => s.trim()).filter(Boolean);

  const handleQuery = async () => {
    const batchNumbers = parseBatchNumbers();
    if (!batchNumbers.length) {
      message.warning('请输入至少一个批号');
      return;
    }
    setLoading(true);
    try {
      const res = await api.post('/declaration/query', { batch_numbers: batchNumbers });
      setResult(res.data);
    } catch (e) {
      message.error(e.response?.data?.detail || '查询失败');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadAll = async () => {
    const batchNumbers = (result?.results || []).map((r) => r.batch_number);
    if (!batchNumbers.length) return;
    setDownloading(true);
    try {
      const res = await api.post(
        '/declaration/download-all',
        { batch_numbers: batchNumbers },
        { responseType: 'blob' },
      );
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = '批号单据.zip';
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      message.error(e.response?.data?.detail || '打包下载失败');
    } finally {
      setDownloading(false);
    }
  };

  const downloadHref = (d) => `${d.download_url}?token=${encodeURIComponent(getToken() || '')}`;

  const columns = [
    { title: '批号', dataIndex: 'batch_number', width: 140 },
    {
      title: '合同号',
      dataIndex: 'contract_number',
      render: (v) => (v ? v : <Text type="secondary">-</Text>),
    },
    { title: '规格型号', dataIndex: 'model', render: (v) => v || '-' },
    {
      title: '状态',
      dataIndex: 'status',
      width: 240,
      render: (v) => {
        if (v === 'found') return <Tag color="green">已匹配</Tag>;
        if (v === 'no_permission') return <Tag color="orange">无该批号的下载权限</Tag>;
        return <Tag color="red">未找到相关批号的文件，请联系商务确认</Tag>;
      },
    },
    {
      title: '资料文件',
      dataIndex: 'documents',
      render: (docs) =>
        docs && docs.length ? (
          <Space direction="vertical" size={2}>
            {docs.map((d) => (
              <a key={d.filename} href={downloadHref(d)} download>
                {d.document_type}
              </a>
            ))}
          </Space>
        ) : (
          <Text type="secondary">无</Text>
        ),
    },
  ];

  return (
    <Card title="检验报告">
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message="输入产品批号（可多行或逗号分隔）进行查询，可下载进口报关单、检疫证明等 PDF 资料。仅限您有采购记录的批号。"
      />
      <Space direction="vertical" style={{ width: '100%' }} size={12}>
        <TextArea
          rows={5}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder={'每行一个批号，例如：\n18483743\n18494576'}
        />
        <Space>
          <Button type="primary" icon={<SearchOutlined />} loading={loading} onClick={handleQuery}>
            查询
          </Button>
          {result && result.documents > 0 && (
            <Button icon={<DownloadOutlined />} loading={downloading} onClick={handleDownloadAll}>
              全部下载（{result.documents} 份）
            </Button>
          )}
        </Space>
      </Space>

      {result && (
        <div style={{ marginTop: 16 }}>
          <Space size={24} style={{ marginBottom: 8 }}>
            <Statistic title="查询" value={result.total} />
            <Statistic title="匹配" value={result.matched} />
            <Statistic title="文件" value={result.documents} />
          </Space>
          <Table
            rowKey="batch_number"
            columns={columns}
            dataSource={result.results}
            size="small"
            pagination={false}
          />
        </div>
      )}
    </Card>
  );
}
